import React, { useMemo } from 'react';

import { parseCountry, type CountryData } from 'react-international-phone';

import useTranslation from '../../../../features/locale/useTranslation';
import { Select } from '../../../Select';
import { Small, Text } from '../../../Typography';
import { LOCALIZED_COUNTRIES } from '../../localizedCountries';
import { FlagImg } from '../FlagImg';

import { flagSelectCountries } from './FlagSelect.data';
import { FlagSelectTrigger } from './FlagSelectTrigger';

import styles from './FlagSelect.module.scss';

export type FlagSelectProps = {
    value: string | undefined;
    onSelect: (value: string) => void;
    preferredCountries?: readonly string[];
    readOnly?: boolean;
    disabled?: boolean;
};

const FILTER_KEY = 'name';

const getPreferredCountriesList = (preferredCountries: readonly string[]) => {
    const preferredSet = new Set(preferredCountries.map((iso2) => iso2.toLowerCase()));
    const preferred: CountryData[] = [];
    const nonPreferred: CountryData[] = [];

    for (const country of flagSelectCountries) {
        const parsedCountry = parseCountry(country);

        if (preferredSet.has(parsedCountry.iso2)) {
            preferred.push(country);
        } else {
            nonPreferred.push(country);
        }
    }
    return [...preferred, ...nonPreferred];
};

export const FlagSelect = ({
    value,
    onSelect,
    preferredCountries,
    readOnly,
    disabled
}: FlagSelectProps) => {
    const { t } = useTranslation();
    const countryOptions = useMemo(() => {
        const countries = preferredCountries
            ? getPreferredCountriesList(preferredCountries)
            : flagSelectCountries;

        return countries.map((c) => {
            const parsedCountry = parseCountry(c);
            const localizedName = LOCALIZED_COUNTRIES[parsedCountry.iso2 as never];
            const rawStringName = `${parsedCountry.name} ${localizedName ?? ''} +${parsedCountry.dialCode}`;
            return {
                label: (
                    <div className={styles.countryOption}>
                        <FlagImg iso2={parsedCountry.iso2} className={styles.flagImage} />
                        <div className={styles.countryNames}>
                            <Text>{parsedCountry.name}</Text>
                            {localizedName && <Small color="secondary">{localizedName}</Small>}
                        </div>
                        <div>
                            <Small color="secondary">+{parsedCountry.dialCode}</Small>
                        </div>
                    </div>
                ),
                value: parsedCountry.iso2,
                [FILTER_KEY]: rawStringName,
                'aria-label': rawStringName
            };
        });
    }, [preferredCountries]);

    return (
        <div className={styles.container}>
            <Select.Root
                value={value}
                onSelect={onSelect}
                options={countryOptions}
                dropdownMatchSelectWidth={440}
                optionFilterProp={FILTER_KEY}
                readOnly={readOnly}
                disabled={disabled}
                aria-label="Open country selector"
            >
                <FlagSelectTrigger />
                <Select.Dropdown>
                    <Select.DropdownSearchInput placeholder={t('Search for country')} />
                    <Select.DropdownEmptyContent />
                    <Select.Options />
                </Select.Dropdown>
            </Select.Root>
        </div>
    );
};
